<template>
  <div class="sr-root">
    <!-- ═══════════ HEADER ═══════════ -->
    <header class="sr-top">
      <button class="sr-back" @click="goBack">
        <i class="ri-arrow-left-line"></i> Volver
      </button>
      <div class="sr-title" v-if="shift">
        <span class="plate">{{ shift.vehicle_plate }}</span>
        <span class="sep">·</span>
        <span class="drv">{{ shift.driver_name }}</span>
      </div>
      <div class="sr-meta" v-if="shift">
        <div class="m"><span class="l">Fecha</span><span class="v">{{ dateText }}</span></div>
        <div class="m"><span class="l">Duración</span><span class="v">{{ durationText }}</span></div>
        <div class="m"><span class="l">Viajes</span><span class="v">{{ summary ? summary.total_rides : 0 }}</span></div>
        <div class="sr-badge" v-if="isActive"><span class="dot"></span>EN VIVO</div>
        <div class="sr-badge closed" v-else>Cerrado</div>
      </div>
    </header>

    <!-- ═══════════ BODY: mapa + panel ═══════════ -->
    <div class="sr-body">
      <div class="sr-map-wrap">
        <div ref="map" class="sr-map"></div>
        <div class="sr-legend">
          <div class="lt">Recorrido</div>
          <div class="li"><span class="ln" style="background:#007bff"></span>En camino</div>
          <div class="li"><span class="ln" style="background:#17a2b8"></span>Ubicado</div>
          <div class="li"><span class="ln" style="background:#28a745"></span>En proceso</div>
          <div class="li"><span class="ln thin" style="background:#adb5bd"></span>Disponible</div>
          <div class="li"><span class="ln" style="background:#F59E0B"></span>Movimiento sin viaje (&gt;200m)</div>
          <div class="li"><span class="ln" style="background:#EF4444"></span>Movimiento sospechoso (&gt;500m)</div>
        </div>
        <div class="sr-loading" v-if="loading">Cargando recorrido…</div>
        <div class="sr-error" v-else-if="error">{{ error }}</div>
      </div>

      <!-- ═══════════ PANEL LATERAL ═══════════ -->
      <aside class="sr-side">
        <div class="sr-side-head">Línea de tiempo</div>

        <div class="sr-timeline">
          <template v-for="item in timeline">
            <!-- Alerta de movimiento sospechoso (posición cronológica) -->
            <div
              v-if="item.type === 'suspicious'"
              class="sr-susp"
              :key="item.key"
              @click="onSuspiciousClick(item.mv)"
            >
              <i class="ri-alert-line"></i>
              <span class="txt">
                Movimiento detectado sin viaje — {{ Number(item.mv.distance_km).toFixed(1) }} km
                en {{ item.mv.duration_min }} min
              </span>
            </div>

            <!-- Evento normal -->
            <template v-else>
              <div class="sr-gap" v-if="item.gap" :key="item.key + '-g'">
                <span class="line"></span>
                <span class="txt">{{ item.gap }}</span>
              </div>

              <div
                class="sr-evt"
                :class="{ sel: selectedEventNumber === item.ev.number, clickable: hasCoords(item.ev) }"
                :data-n="item.ev.number"
                :key="item.key"
                @click="onTimelineClick(item.ev)"
              >
                <div class="ic" :style="{ background: eventMeta(item.ev.action).color }">
                  <span v-if="eventMeta(item.ev.action).symbol">{{ eventMeta(item.ev.action).symbol }}</span>
                  <span v-else>{{ item.ev.number }}</span>
                </div>
                <div class="body">
                  <div class="row1">
                    <span class="lbl">{{ eventMeta(item.ev.action).label }}</span>
                    <span class="hr">{{ hourText(item.ev.time) }}</span>
                  </div>
                  <div class="row2" v-if="item.ev.ride_code || item.ev.detail">
                    <span v-if="item.ev.ride_code" class="code">{{ item.ev.ride_code }}</span>
                    <span v-if="item.ev.detail" class="det">{{ item.ev.detail }}</span>
                  </div>
                </div>
              </div>
            </template>
          </template>

          <div class="sr-empty" v-if="!loading && !events.length">Sin eventos en este turno.</div>
        </div>

        <!-- ═══════════ RESUMEN ═══════════ -->
        <div class="sr-summary" v-if="summary">
          <div class="sr-summary-head">Resumen del turno</div>
          <div class="grid">
            <div class="c"><span class="l">Duración</span><span class="v">{{ durationText }}</span></div>
            <div class="c"><span class="l">Viajes</span><span class="v">{{ summary.total_rides }}</span></div>
            <div class="c"><span class="l">Non shows</span><span class="v">{{ summary.non_shows }}</span></div>
            <div class="c"><span class="l">Distancia</span><span class="v">{{ distanceText }}</span></div>
            <div class="c"><span class="l">En servicio</span><span class="v">{{ summary.time_in_service }}</span></div>
            <div class="c"><span class="l">Disponible</span><span class="v">{{ summary.time_available }}</span></div>
            <div class="c"><span class="l">Prom. / viaje</span><span class="v">{{ summary.avg_time_per_ride }}</span></div>
            <div class="c"><span class="l">Prom. espera</span><span class="v">{{ summary.avg_wait_time }}</span></div>
            <div class="c wide">
              <span class="l">Distancia sin viaje</span>
              <span class="v" :class="hasSuspiciousDistance ? 'danger' : 'ok'">
                <template v-if="hasSuspiciousDistance">⚠️ {{ distanceWithoutRideText }}</template>
                <template v-else>0 km ✓</template>
              </span>
            </div>
            <div class="c wide">
              <span class="l">Odómetro</span>
              <span class="v odo" :class="odometerClass">{{ odometerText }}</span>
            </div>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import { Alert } from 'wize-admin'
import { ShiftRouteService } from '@/core/services'
import { loadGoogleMaps, MAP_ID } from '@/core/utils/googleMaps'

const LIMA_CENTER = { lat: -12.046, lng: -77.043 }

// Umbrales (metros) para colorear movimiento en estado available (DEF-ADM-013).
// Ajustables aquí.
const AVAILABLE_MOVE_RED_M = 500    // > 500m entre puntos → rojo (sospechoso)
const AVAILABLE_MOVE_ORANGE_M = 200 // > 200m entre puntos → naranja (sin viaje)

// Estilo de la polyline por clave de segmento (estado + tramo de movimiento).
const SEG_STYLE = {
  en_camino:        { color: '#007bff', weight: 4, opacity: 0.95 },
  ubicado:          { color: '#17a2b8', weight: 4, opacity: 0.95 },
  en_proceso:       { color: '#28a745', weight: 4, opacity: 0.95 },
  available:        { color: '#adb5bd', weight: 2, opacity: 0.6 },
  available_orange: { color: '#F59E0B', weight: 4, opacity: 0.95 },
  available_red:    { color: '#EF4444', weight: 4, opacity: 0.95 }
}

// Metadatos del marcador por tipo de evento. size 'lg' = hito de turno.
const EVENT_META = {
  SHIFT_START:      { color: '#25C26E', size: 'lg', label: 'Inicio turno',   symbol: '' },
  SHIFT_END:        { color: '#dc3545', size: 'lg', label: 'Fin turno',      symbol: '' },
  SHIFT_FORCED_END: { color: '#dc3545', size: 'lg', label: 'Cierre forzado', symbol: '' },
  RIDE_ASSIGNED:    { color: '#6c757d', size: 'sm', label: 'Viaje asignado', symbol: '' },
  RIDE_EN_CAMINO:   { color: '#007bff', size: 'sm', label: 'En camino',      symbol: '' },
  // color-violet, el mismo UBICADO que las otras pantallas. Antes era #17a2b8,
  // a 31.6 de RIDE_ASSIGNED; ahora el marcador más parecido queda a 43.3.
  RIDE_UBICADO:     { color: '#4D3C81', size: 'sm', label: 'Ubicado',        symbol: '' },
  RIDE_EN_PROCESO:  { color: '#f59e0b', size: 'sm', label: 'En proceso',     symbol: '' },
  RIDE_FINALIZADO:  { color: '#28a745', size: 'sm', label: 'Finalizado',     symbol: '✓' },
  RIDE_NON_SHOW:    { color: '#dc3545', size: 'sm', label: 'No show',        symbol: '✗' },
  RIDE_CANCELADO:   { color: '#dc3545', size: 'sm', label: 'Cancelado',      symbol: '✗' }
}

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

export default {
  name: 'ShiftRoutePage',
  data() {
    return {
      loading: true,
      error: null,
      shift: null,
      gpsPoints: [],
      events: [],
      summary: null,
      suspiciousMovements: [],
      selectedEventNumber: null
    }
  },
  computed: {
    cityTz() {
      const city = this.$store.getters['city/getCurrentCity']
      return city && city.timezone ? city.timezone : 'America/Lima'
    },
    isActive() {
      return !!(this.shift && !this.shift.ended_at)
    },
    dateText() {
      if (!this.shift) return '—'
      const d = new Date(this.shift.started_at)
      if (isNaN(d.getTime())) return '—'
      return d.toLocaleDateString('es-PE', {
        timeZone: this.cityTz, day: '2-digit', month: '2-digit', year: 'numeric'
      })
    },
    durationText() {
      if (!this.shift) return '—'
      const s = new Date(this.shift.started_at).getTime()
      const e = this.shift.ended_at ? new Date(this.shift.ended_at).getTime() : Date.now()
      if (isNaN(s) || isNaN(e) || e < s) return '—'
      const min = Math.round((e - s) / 60000)
      const h = Math.floor(min / 60)
      const m = min % 60
      return h ? `${h}h ${m}min` : `${m}min`
    },
    distanceText() {
      if (!this.summary) return '—'
      return `${Number(this.summary.total_distance_km || 0).toFixed(2)} km`
    },
    // Texto de la distancia sin viaje para el resumen.
    distanceWithoutRideText() {
      const km = this.summary ? Number(this.summary.distance_without_ride_km || 0) : 0
      return `${km.toFixed(1)} km`
    },
    hasSuspiciousDistance() {
      return this.summary && Number(this.summary.distance_without_ride_km || 0) > 0
    },
    // Odómetro (DEF-ADM-014): "25,290 → 25,412 km (Δ122 km | GPS: 98.5 km |
    // Discrepancia: +23.5 km)". "Sin registro" si falta el km de inicio o fin.
    odometerText() {
      const s = this.summary
      if (!s || s.start_odometer_km == null || s.end_odometer_km == null) return 'Sin registro'
      const start = Number(s.start_odometer_km).toLocaleString('en-US')
      const end = Number(s.end_odometer_km).toLocaleString('en-US')
      const delta = Number(s.odometer_delta_km || 0).toLocaleString('en-US')
      const gps = Number(s.total_distance_km || 0).toFixed(1)
      const disc = Number(s.distance_discrepancy_km || 0)
      const discStr = (disc > 0 ? '+' : '') + disc.toFixed(1)
      return `${start} → ${end} km (Δ${delta} km | GPS: ${gps} km | Discrepancia: ${discStr} km)`
    },
    // Color por magnitud de la discrepancia: >25 rojo, >10 naranja, resto verde.
    odometerClass() {
      const s = this.summary
      if (!s || s.start_odometer_km == null || s.end_odometer_km == null || s.distance_discrepancy_km == null) return ''
      const abs = Math.abs(Number(s.distance_discrepancy_km))
      if (abs > 25) return 'danger'
      if (abs > 10) return 'warn'
      return 'ok'
    },
    // Timeline cronológico: eventos + alertas de movimiento sospechoso,
    // ordenados por tiempo. Los eventos llevan el gap contra el evento previo.
    timeline() {
      const items = []
      this.events.forEach(ev => {
        items.push({ type: 'event', time: new Date(ev.time).getTime(), ev, key: 'e' + ev.number })
      })
      this.suspiciousMovements.forEach((mv, i) => {
        items.push({ type: 'suspicious', time: new Date(mv.start_time).getTime(), mv, key: 's' + i })
      })
      items.sort((a, b) => a.time - b.time)
      let lastEvTime = null
      items.forEach(it => {
        if (it.type === 'event') {
          it.gap = lastEvTime != null ? this.gapMs(lastEvTime, it.time) : ''
          lastEvTime = it.time
        }
      })
      return items
    }
  },
  methods: {
    // ── Formato / helpers ──────────────────────────────────────────
    eventMeta(action) {
      return EVENT_META[action] || { color: '#6c757d', size: 'sm', label: action, symbol: '' }
    },
    hourText(value) {
      if (!value) return ''
      const d = new Date(value)
      if (isNaN(d.getTime())) return ''
      return d.toLocaleTimeString('es-PE', {
        timeZone: this.cityTz, hour: '2-digit', minute: '2-digit', hour12: false
      })
    },
    hasCoords(ev) {
      return ev.lat != null && ev.lng != null
    },
    // Tiempo transcurrido entre dos instantes (ms), en texto ("5min" / "1h 10min").
    gapMs(a, b) {
      if (isNaN(a) || isNaN(b) || b <= a) return ''
      const min = Math.round((b - a) / 60000)
      if (min <= 0) return ''
      const h = Math.floor(min / 60)
      const m = min % 60
      return h ? `${h}h ${m}min` : `${m}min`
    },

    // ── Data ───────────────────────────────────────────────────────
    async load(isRefresh) {
      try {
        const res = await ShiftRouteService.getShiftRoute(this.$route.params.id)
        const p = res.payload || {}
        this.shift = p.shift || null
        this.events = p.events || []
        this.summary = p.summary || null
        this.suspiciousMovements = p.suspicious_movements || []
        const newPoints = p.gps_points || []

        if (!isRefresh) {
          this.gpsPoints = newPoints
          this.$nextTick(() => this.renderAll())
        } else {
          // Refresh de turno activo: extender sin redibujar todo.
          const from = this.gpsPoints.length ? this.gpsPoints.length - 1 : 0
          this.gpsPoints = newPoints
          this.renderRoute(from)
          this.renderMarkers()
          this.updateLiveMarker()
        }
        this.error = null
      } catch (e) {
        console.error(e)
        this.error = e.errors ? e.errors.message : 'No se pudo cargar el recorrido del turno'
        if (!isRefresh) Alert.error(this.error)
      } finally {
        this.loading = false
        // Si el turno ya está cerrado, no tiene sentido seguir refrescando.
        if (!this.isActive) this.stopRefresh()
      }
    },

    // ── Mapa ───────────────────────────────────────────────────────
    async initMap() {
      const { maps, marker, core } = await loadGoogleMaps()
      this.maps = maps
      this.marker = marker
      this.core = core
      this.map = new maps.Map(this.$refs.map, {
        center: LIMA_CENTER,
        zoom: 12,
        mapId: MAP_ID,
        colorScheme: 'DARK',
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true
      })
      this.infoWindow = new maps.InfoWindow()
      this.routePolylines = []
    },
    // Limpieza de overlays (Google no tiene layerGroup.clearLayers).
    clearRoute() {
      if (this.routePolylines) this.routePolylines.forEach(p => p.setMap(null))
      this.routePolylines = []
    },
    clearMarkers() {
      Object.values(this.eventMarkers).forEach(e => { if (e.marker) e.marker.map = null })
      this.eventMarkers = {}
      this.renderedEvents = new Set()
      if (this.liveMarker) { this.liveMarker.map = null; this.liveMarker = null }
    },
    renderAll() {
      if (!this.map) return
      this.clearRoute()
      this.clearMarkers()
      this.lastPointIndex = 0
      this.renderRoute(0)
      this.renderMarkers()
      this.updateLiveMarker()
      this.fitBounds()
    },
    // Dibuja los segmentos de polyline desde fromIndex. Agrupa puntos GPS
    // consecutivos con el mismo ride_status; cada grupo arranca desde el último
    // punto del grupo previo para que las líneas queden conectadas.
    renderRoute(fromIndex) {
      const pts = this.gpsPoints
      if (fromIndex >= pts.length - 1) {
        this.lastPointIndex = Math.max(0, pts.length - 1)
        return
      }
      const groups = []
      for (let i = fromIndex; i < pts.length; i++) {
        const p = pts[i]
        const key = this.segKey(p)
        const last = groups[groups.length - 1]
        if (!last || last.key !== key) {
          const g = { key, latlngs: [] }
          if (last) g.latlngs.push(last.latlngs[last.latlngs.length - 1]) // conectar
          g.latlngs.push({ lat: p.lat, lng: p.lng })
          groups.push(g)
        } else {
          last.latlngs.push({ lat: p.lat, lng: p.lng })
        }
      }
      groups.forEach(g => {
        if (g.latlngs.length < 2) return
        const s = SEG_STYLE[g.key] || SEG_STYLE.available
        const line = new this.maps.Polyline({
          path: g.latlngs,
          strokeColor: s.color,
          strokeWeight: s.weight,
          strokeOpacity: s.opacity,
          map: this.map
        })
        this.routePolylines.push(line)
      })
      this.lastPointIndex = pts.length - 1
    },
    // Clave de estilo del tramo que llega a este punto. En available, el color
    // depende de distance_from_prev (movimiento sin viaje); el resto usa su estado.
    segKey(p) {
      const st = p.ride_status || 'available'
      if (st !== 'available') return st
      const d = p.distance_from_prev || 0
      if (d > AVAILABLE_MOVE_RED_M) return 'available_red'
      if (d > AVAILABLE_MOVE_ORANGE_M) return 'available_orange'
      return 'available'
    },
    // Añade marcadores para eventos con coordenadas que aún no se dibujaron.
    renderMarkers() {
      this.events.forEach(ev => {
        if (!this.hasCoords(ev) || this.renderedEvents.has(ev.number)) return
        const m = new this.marker.AdvancedMarkerElement({
          map: this.map,
          position: { lat: ev.lat, lng: ev.lng },
          content: this.buildEventContent(ev),
          gmpClickable: true
        })
        m.addListener('gmp-click', () => this.selectEvent(ev.number, true))
        this.eventMarkers[ev.number] = { marker: m, ev }
        this.renderedEvents.add(ev.number)
      })
    },
    // Contenido DOM del AdvancedMarker de evento: mismo HTML/clases que el divIcon
    // (número/símbolo, sm/lg, outline de selección). translateY(50%) centra el
    // círculo sobre la coordenada (AdvancedMarker ancla abajo-centro).
    buildEventContent(ev) {
      const meta = this.eventMeta(ev.action)
      const big = meta.size === 'lg'
      const inner = meta.symbol || ev.number
      const sel = this.selectedEventNumber === ev.number ? ' sel' : ''
      const el = document.createElement('div')
      el.className = `sr-mk ${big ? 'lg' : 'sm'}${sel}`
      el.style.background = meta.color
      el.style.transform = 'translateY(50%)'
      el.textContent = String(inner)
      return el
    },
    eventPopup(ev) {
      const meta = this.eventMeta(ev.action)
      const code = ev.ride_code ? `<div class="prow"><span>Viaje</span><b>${esc(ev.ride_code)}</b></div>` : ''
      const det = ev.detail ? `<div class="prow"><span>Detalle</span><b>${esc(ev.detail)}</b></div>` : ''
      return `<div class="sr-pop">
        <h3>#${ev.number} · ${esc(meta.label)}</h3>
        <div class="prow"><span>Hora</span><b>${esc(this.hourText(ev.time))}</b></div>
        ${code}${det}
      </div>`
    },
    // Marcador pulsante en el último punto GPS (solo turno activo).
    updateLiveMarker() {
      if (!this.map || !this.marker) return
      if (this.liveMarker) { this.liveMarker.map = null; this.liveMarker = null }
      if (!this.isActive || !this.gpsPoints.length) return
      const last = this.gpsPoints[this.gpsPoints.length - 1]
      const el = document.createElement('div')
      el.className = 'sr-live'
      el.style.transform = 'translateY(50%)'
      el.innerHTML = '<span class="halo"></span><span class="core"></span>'
      this.liveMarker = new this.marker.AdvancedMarkerElement({
        map: this.map,
        position: { lat: last.lat, lng: last.lng },
        content: el,
        zIndex: 1000
      })
    },
    fitBounds() {
      if (!this.map || !this.core) return
      const coords = this.gpsPoints.map(p => ({ lat: p.lat, lng: p.lng }))
      this.events.forEach(ev => { if (this.hasCoords(ev)) coords.push({ lat: ev.lat, lng: ev.lng }) })
      if (coords.length) {
        const bounds = new this.core.LatLngBounds()
        coords.forEach(c => bounds.extend(c))
        this.map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 })
        this.core.event.addListenerOnce(this.map, 'idle', () => {
          if (this.map.getZoom() > 16) this.map.setZoom(16)
        })
      } else {
        this.map.setCenter(LIMA_CENTER)
        this.map.setZoom(12)
      }
    },

    // ── Sincronización timeline ↔ mapa ─────────────────────────────
    onTimelineClick(ev) {
      if (!this.hasCoords(ev)) { this.selectedEventNumber = ev.number; return }
      this.selectEvent(ev.number, false)
    },
    // Click en una alerta de movimiento → centra el mapa en el tramo.
    onSuspiciousClick(mv) {
      if (!this.map || !this.core || mv.start_lat == null) return
      const bounds = new this.core.LatLngBounds()
      bounds.extend({ lat: mv.start_lat, lng: mv.start_lng })
      bounds.extend({ lat: mv.end_lat, lng: mv.end_lng })
      this.map.fitBounds(bounds, { top: 80, right: 80, bottom: 80, left: 80 })
      this.core.event.addListenerOnce(this.map, 'idle', () => {
        if (this.map.getZoom() > 16) this.map.setZoom(16)
      })
    },
    // fromMap=true → click en marcador; fromMap=false → click en timeline.
    selectEvent(number, fromMap) {
      const prev = this.selectedEventNumber
      this.selectedEventNumber = number
      this.refreshIcon(prev)
      this.refreshIcon(number)

      const entry = this.eventMarkers[number]
      if (!entry) { this.scrollTimelineTo(number); return }
      this.scrollTimelineTo(number)
      // Desde el timeline: centramos el mapa (panTo + zoom; Google no tiene flyTo).
      if (!fromMap) {
        this.map.panTo(entry.marker.position)
        this.map.setZoom(16)
      }
      this.infoWindow.setContent(this.eventPopup(entry.ev))
      this.infoWindow.open({ map: this.map, anchor: entry.marker })
    },
    refreshIcon(number) {
      const entry = this.eventMarkers[number]
      if (entry) entry.marker.content = this.buildEventContent(entry.ev)
    },
    scrollTimelineTo(number) {
      this.$nextTick(() => {
        const el = this.$el.querySelector(`.sr-evt[data-n="${number}"]`)
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },

    // ── Refresco (turno activo) ────────────────────────────────────
    startRefresh() {
      if (this.refreshTimer) return
      this.refreshTimer = setInterval(() => {
        if (!document.hidden && this.isActive) this.load(true)
      }, 30000)
    },
    stopRefresh() {
      if (this.refreshTimer) { clearInterval(this.refreshTimer); this.refreshTimer = null }
    },

    goBack() {
      this.$router.back()
    },
    adjustSize() {
      const el = this.$el
      if (!el || !el.getBoundingClientRect) return
      const top = el.getBoundingClientRect().top
      el.style.height = Math.max(400, Math.round(window.innerHeight - top)) + 'px'
      // Google Maps recalcula el tamaño del contenedor automáticamente.
    }
  },
  created() {
    // Objetos del mapa: NO reactivos.
    this.maps = null
    this.marker = null
    this.core = null
    this.map = null
    this.infoWindow = null
    this.routePolylines = []
    this.eventMarkers = {}
    this.renderedEvents = new Set()
    this.liveMarker = null
    this.lastPointIndex = 0
    this.refreshTimer = null
  },
  async mounted() {
    document.body.classList.add('tracking-active')
    await this.initMap()
    await this.load(false)
    if (this.isActive) this.startRefresh()
    window.addEventListener('resize', this.adjustSize)
    this.$nextTick(() => {
      this.$store.commit('app/setMenu', false)
      this.adjustSize()
      setTimeout(this.adjustSize, 850)
    })
  },
  beforeDestroy() {
    document.body.classList.remove('tracking-active')
    this.stopRefresh()
    window.removeEventListener('resize', this.adjustSize)
    this.$store.commit('app/setMenu', true)
    this.clearRoute()
    this.clearMarkers()
    if (this.infoWindow) this.infoWindow.close()
    this.infoWindow = null
    // Google Maps no expone destroy(); soltamos la referencia.
    this.map = null
  }
}
</script>

<!-- Estilos dark namespaced bajo .sr-root (NO scoped: markers/popups se inyectan
     por JS; el prefijo sr- evita fugas al admin light). -->
<style lang="stylus">
.sr-root
  --tk-base #0B0E14
  --tk-surface #141922
  --tk-surface-2 #1B2230
  --tk-border #232C3B
  --tk-border-soft #1C2431
  --tk-text #E7ECF3
  --tk-muted #8592A6
  --tk-muted-2 #5C6675
  --tk-accent #F5A524
  --tk-green #25C26E
  position relative
  display flex
  flex-direction column
  height 100%
  width 100%
  background var(--tk-base)
  color var(--tk-text)
  overflow hidden
  font-family 'Inter', system-ui, sans-serif

  *
    box-sizing border-box

  // ---- Header ----
  .sr-top
    display flex
    align-items center
    gap 16px
    padding 0 18px
    height 56px
    flex-shrink 0
    background var(--tk-surface)
    border-bottom 1px solid var(--tk-border)
  .sr-back
    display flex
    align-items center
    gap 6px
    font-size 13px
    font-weight 600
    color var(--tk-text)
    background var(--tk-surface-2)
    border 1px solid var(--tk-border)
    border-radius 8px
    padding 7px 12px
    cursor pointer
    transition background .15s
    &:hover
      background var(--tk-border)
  .sr-title
    display flex
    align-items center
    gap 8px
    .plate
      font-size 16px
      font-weight 700
    .sep
      color var(--tk-muted-2)
    .drv
      font-size 14px
      color var(--tk-muted)
  .sr-meta
    margin-left auto
    display flex
    align-items center
    gap 22px
    .m
      display flex
      flex-direction column
      line-height 1.15
      .l
        font-size 10px
        color var(--tk-muted)
        text-transform uppercase
        letter-spacing .09em
      .v
        font-size 15px
        font-weight 700
        margin-top 2px
  .sr-badge
    display flex
    align-items center
    gap 7px
    font-size 11px
    font-weight 700
    letter-spacing .12em
    color var(--tk-green)
    padding 5px 11px
    border-radius 999px
    background rgba(37,194,110,.10)
    border 1px solid rgba(37,194,110,.25)
    .dot
      width 8px
      height 8px
      border-radius 50%
      background var(--tk-green)
      animation srPulse 1.8s infinite
    &.closed
      color var(--tk-muted)
      background rgba(133,146,166,.10)
      border-color rgba(133,146,166,.25)

  // ---- Body ----
  .sr-body
    flex 1
    display flex
    min-height 0

  // ---- Map ----
  .sr-map-wrap
    flex 1
    position relative
    min-width 0
  .sr-map
    position absolute
    inset 0
    background var(--tk-base)
  .sr-legend
    position absolute
    bottom 14px
    left 14px
    z-index 500
    background rgba(20,25,34,.92)
    border 1px solid var(--tk-border)
    border-radius 11px
    padding 11px 13px
    .lt
      font-size 10px
      text-transform uppercase
      letter-spacing .1em
      color var(--tk-muted)
      margin-bottom 7px
    .li
      display flex
      align-items center
      gap 8px
      font-size 12px
      padding 2px 0
      .ln
        width 18px
        height 4px
        border-radius 2px
        &.thin
          height 2px
  .sr-loading, .sr-error
    position absolute
    top 14px
    left 50%
    transform translateX(-50%)
    z-index 600
    background rgba(20,25,34,.95)
    border 1px solid var(--tk-border)
    border-radius 9px
    padding 8px 16px
    font-size 13px
    color var(--tk-muted)
  .sr-error
    color #F98A8A
    border-color rgba(239,68,68,.45)

  // ---- Panel lateral ----
  .sr-side
    width 360px
    flex-shrink 0
    display flex
    flex-direction column
    background var(--tk-surface)
    border-left 1px solid var(--tk-border)
    min-height 0
  .sr-side-head
    flex-shrink 0
    padding 14px 16px
    font-size 13px
    font-weight 700
    letter-spacing .04em
    text-transform uppercase
    color var(--tk-muted)
    border-bottom 1px solid var(--tk-border-soft)
  .sr-timeline
    flex 1
    overflow-y auto
    padding 10px 12px
    &::-webkit-scrollbar
      width 8px
    &::-webkit-scrollbar-thumb
      background var(--tk-border)
      border-radius 8px
  .sr-gap
    display flex
    align-items center
    gap 8px
    padding 2px 0 2px 15px
    margin 1px 0
    .line
      width 2px
      height 16px
      background var(--tk-border)
      margin-left 4px
    .txt
      font-size 11px
      color var(--tk-muted-2)
  .sr-evt
    display flex
    gap 11px
    align-items flex-start
    padding 9px 10px
    border-radius 10px
    border 1px solid transparent
    transition background .15s, border-color .15s
    &.clickable
      cursor pointer
    &.clickable:hover
      background var(--tk-surface-2)
    &.sel
      background var(--tk-surface-2)
      border-color var(--tk-accent)
    .ic
      width 26px
      height 26px
      flex-shrink 0
      border-radius 50%
      display flex
      align-items center
      justify-content center
      font-size 12px
      font-weight 700
      color #fff
      box-shadow 0 1px 3px rgba(0,0,0,.4)
    .body
      min-width 0
      flex 1
      .row1
        display flex
        justify-content space-between
        gap 10px
        align-items baseline
        .lbl
          font-size 13px
          font-weight 600
        .hr
          font-size 12px
          color var(--tk-muted)
          font-variant-numeric tabular-nums
          white-space nowrap
      .row2
        margin-top 2px
        font-size 11px
        color var(--tk-muted-2)
        .code
          color var(--tk-accent)
          font-weight 600
          margin-right 6px
        .det
          word-break break-word
  .sr-susp
    display flex
    align-items center
    gap 8px
    margin 4px 0
    padding 9px 11px
    border-radius 10px
    font-size 12px
    font-weight 600
    color #F98A8A
    background rgba(239,68,68,.12)
    border 1px solid rgba(239,68,68,.4)
    cursor pointer
    transition background .15s
    i
      font-size 15px
      flex-shrink 0
    .txt
      min-width 0
    &:hover
      background rgba(239,68,68,.2)
      color #fff
  .sr-empty
    text-align center
    color var(--tk-muted-2)
    font-size 13px
    padding 30px 12px

  // ---- Resumen ----
  .sr-summary
    flex-shrink 0
    border-top 1px solid var(--tk-border)
    padding 12px 14px
    background var(--tk-surface-2)
  .sr-summary-head
    font-size 11px
    font-weight 700
    text-transform uppercase
    letter-spacing .08em
    color var(--tk-muted)
    margin-bottom 10px
  .sr-summary .grid
    display grid
    grid-template-columns 1fr 1fr
    gap 8px 14px
    .c
      display flex
      flex-direction column
      &.wide
        grid-column 1 / -1
      .l
        font-size 10px
        color var(--tk-muted-2)
        text-transform uppercase
        letter-spacing .05em
      .v
        font-size 14px
        font-weight 700
        margin-top 1px
        &.danger
          color #F98A8A
        &.warn
          color #F7BC5A
        &.ok
          color var(--tk-green)
        &.odo
          font-size 12px
          line-height 1.35

// ---- Google Maps InfoWindow (dark, bajo .sr-root) ----
.sr-root .sr-map
  .gm-style-iw-c
    background #141922 !important
    border 1px solid #232C3B
    border-radius 12px !important
    box-shadow 0 6px 20px rgba(0,0,0,.5) !important
    padding 0 !important
  .gm-style-iw-d
    overflow hidden !important
    color #E7ECF3
  .gm-style-iw-tc::after
    background #141922 !important
  .gm-ui-hover-effect > span
    background-color #8592A6 !important

// ---- Marcadores de evento (inyectados por JS, bajo .sr-root) ----
.sr-root .sr-mk
  display flex
  align-items center
  justify-content center
  border-radius 50%
  color #fff
  font-weight 700
  border 2px solid rgba(11,14,20,.85)
  box-shadow 0 2px 5px rgba(0,0,0,.5)
  &.sm
    width 22px
    height 22px
    font-size 11px
  &.lg
    width 30px
    height 30px
    font-size 13px
    border-width 3px
  &.sel
    outline 2px solid #F5A524
    outline-offset 1px
.sr-root .sr-live
  position relative
  width 20px
  height 20px
  .core
    position absolute
    top 50%
    left 50%
    width 12px
    height 12px
    margin -6px 0 0 -6px
    border-radius 50%
    background #25C26E
    border 2px solid #0B0E14
  .halo
    position absolute
    top 50%
    left 50%
    width 20px
    height 20px
    margin -10px 0 0 -10px
    border-radius 50%
    background rgba(37,194,110,.5)
    animation srHalo 1.8s ease-out infinite

// ---- Popup interno (bajo .sr-root) ----
.sr-root .sr-pop
  h3
    font-size 14px
    margin-bottom 6px
  .prow
    display flex
    justify-content space-between
    gap 16px
    font-size 12px
    padding 3px 0
    border-top 1px solid #1C2431
    span:first-child
      color #8592A6

@keyframes srPulse
  0%
    box-shadow 0 0 0 0 rgba(37,194,110,.5)
  70%
    box-shadow 0 0 0 9px rgba(37,194,110,0)
  100%
    box-shadow 0 0 0 0 rgba(37,194,110,0)
@keyframes srHalo
  0%
    transform scale(.5)
    opacity .6
  100%
    transform scale(1.6)
    opacity 0

@media (prefers-reduced-motion: reduce)
  .sr-root .sr-live .halo, .sr-root .sr-badge .dot
    animation none

// ---- Layout: la vista llena el área de contenido de PageBase ----
body.tracking-active .wrapper-page__content
  min-height 0 !important
  padding-bottom 0 !important
  overflow hidden !important
body.tracking-active footer
  display none !important
</style>
